/**
 * inbox-humanize.ts · Wave C + Wave POS
 *
 * Traduz tipos técnicos do Inbox (kind, tier, payload) para linguagem
 * operacional em PT-BR. Nunca vaza drift_resolve, workflow_self_heal,
 * IDs internos, PM_PROPOSALS etc. para a UI do PM.
 *
 * Funções exportadas:
 *   humanizeKind(kind)         → { label, description }
 *   humanizeTitle(item)        → título humano PT-BR (NEVER leaks raw kind enums)
 *   humanizeImpact(item)       → frase de impacto operacional
 *   humanizeSuggestion(item)   → ação concreta proposta
 *   humanizePrimaryLabel(item) → label do botão primário
 *   relativeTime(iso)          → "há 2 horas" / "ontem" / "em 2 horas"
 *   sanitizeImpactReason(raw)  → null when technical, trimmed when human
 *   isReasonHuman(raw)         → boolean (inverse of null-check)
 *
 * Wave POS fix (2026-05-17): /inbox still leaked `stage.slip.warn` and other
 * raw kind enums in item.title. Added humanizeTitle() which sanitizes the
 * backend-provided title and falls back to humanizeKind().label so the PM
 * never sees a technical identifier. Applied in InboxCard, HojePage aria-labels,
 * QueueItemRow, ObraCockpit, RecommendedActionCard, NotificationDropdown.
 */

import type { InboxItemDTO, InboxItemKind } from '@builder/shared';
import { getLocale } from '@/i18n';

// ─── Kind labels ─────────────────────────────────────────────────────

interface KindMeta {
  label: string;
  description: string;
}

type L = { pt: KindMeta; en: KindMeta };

const KIND_MAP: Record<InboxItemKind, L> = {
  proposal_pending: {
    pt: { label: 'Sugestão', description: 'Uma sugestão automática aguarda sua decisão.' },
    en: { label: 'Suggestion', description: 'An automatic suggestion is waiting for your decision.' },
  },
  client_error_open: {
    pt: { label: 'Ponto de atenção', description: 'Algo na operação precisa de revisão.' },
    en: { label: 'Attention needed', description: 'Something in the operation needs review.' },
  },
  self_heal_failed: {
    pt: { label: 'Correção automática', description: 'Uma correção automática não conseguiu ser aplicada.' },
    en: { label: 'Automatic fix failed', description: 'An automatic fix could not be applied.' },
  },
  anomaly_pending: {
    pt: { label: 'Ponto de atenção', description: 'Um ponto fora do padrão foi identificado.' },
    en: { label: 'Attention needed', description: 'An unusual activity was identified.' },
  },
  po_pending: {
    pt: { label: 'Compra pendente', description: 'Uma ordem de compra aguarda aprovação.' },
    en: { label: 'Purchase pending', description: 'A purchase order is awaiting approval.' },
  },
  rnc_unassigned: {
    pt: { label: 'Não conformidade', description: 'Uma não conformidade ainda não tem responsável.' },
    en: { label: 'Non-conformance', description: 'A non-conformance has no assignee yet.' },
  },
  fvs_overdue: {
    pt: { label: 'Vistoria pendente', description: 'Uma vistoria está atrasada.' },
    en: { label: 'Inspection overdue', description: 'An inspection is overdue.' },
  },
  doc_expiring: {
    pt: { label: 'Documento vencendo', description: 'Um documento importante está próximo do vencimento.' },
    en: { label: 'Document expiring', description: 'An important document is close to expiry.' },
  },
  anomaly_high: {
    pt: { label: 'Ponto de atenção', description: 'Um desvio significativo foi detectado.' },
    en: { label: 'Attention needed', description: 'A significant deviation was detected.' },
  },
  rdo_missing: {
    pt: { label: 'RDO atrasado', description: 'O diário de obra ainda não foi registrado.' },
    en: { label: 'Daily log missing', description: 'The daily site log has not been recorded yet.' },
  },
  medicao_draft: {
    pt: { label: 'Medição rascunho', description: 'Uma medição está em rascunho e precisa ser finalizada.' },
    en: { label: 'Draft measurement', description: 'A measurement is in draft and needs to be finalized.' },
  },
};

/**
 * Returns the humanized label and description for an item kind.
 * Locale is resolved at call time. Generic fallback if the kind is not recognized.
 */
export function humanizeKind(kind: string): KindMeta {
  const isEn = getLocale() === 'en';
  const entry = KIND_MAP[kind as InboxItemKind];
  if (entry) return isEn ? entry.en : entry.pt;
  return isEn
    ? { label: 'Pending item', description: 'An item needs your attention.' }
    : { label: 'Pendência', description: 'Um item precisa da sua atenção.' };
}

// ─── Title sanitization (Wave POS fix) ───────────────────────────────
//
// Backend sometimes sends item.title as a raw kind enum string like
// `stage.slip.warn`, `budget.overrun`, etc. This blocklist maps
// known technical title patterns to human PT-BR equivalents.
// For unknown technical titles, we fall back to humanizeKind().label.

type BlocklistEntry = { pattern: RegExp; pt: string; en: string };

const KIND_REGEX_BLOCKLIST: Array<BlocklistEntry> = [
  { pattern: /\bstage\.slip(?:\.warn)?\b/i,         pt: 'Etapa atrasando',                  en: 'Stage falling behind' },
  { pattern: /\bbudget\.overrun\b/i,                 pt: 'Orçamento estourou',               en: 'Budget overrun' },
  { pattern: /\brdo\.missing\b/i,                    pt: 'RDO atrasado',                     en: 'Daily log missing' },
  { pattern: /\bcost\.variance(?:\.high)?\b/i,       pt: 'Custo fora do previsto',           en: 'Cost above estimate' },
  { pattern: /\bquality\.rejection(?:\.spike)?\b/i,  pt: 'Pico de rejeição na qualidade',    en: 'Quality rejection spike' },
  { pattern: /\bsupplier\.no_?response\b/i,          pt: 'Fornecedor sem resposta',          en: 'Vendor not responding' },
  { pattern: /\bstage\.slip\b/i,                     pt: 'Etapa atrasando',                  en: 'Stage falling behind' },
  { pattern: /\bcost\.overrun\b/i,                   pt: 'Custo acima do previsto',          en: 'Cost overrun' },
  { pattern: /\bschedule\.slip\b/i,                  pt: 'Cronograma atrasando',             en: 'Schedule slipping' },
  { pattern: /\bworkflow\.stall\b/i,                 pt: 'Fluxo parado',                     en: 'Workflow stalled' },
  { pattern: /\bpolicy\.breach\b/i,                  pt: 'Regra de proteção ativada',        en: 'Protection rule triggered' },
  { pattern: /\bsignal\.alert\b/i,                   pt: 'Alerta detectado',                 en: 'Alert detected' },
  { pattern: /\btrigger\.fired\b/i,                  pt: 'Alerta disparado',                 en: 'Alert triggered' },
];

/**
 * KIND_TITLE_OVERRIDES produces context-enriched titles using evidence
 * fields (dueAt, amount chips). These run only when the base title is
 * already sanitized (human text), giving more precise copy when possible.
 * Locale is resolved at call time inside each override function.
 */
const KIND_TITLE_OVERRIDES: Partial<
  Record<InboxItemKind, (item: InboxItemDTO) => string | null>
> = {
  fvs_overdue: (item) => {
    if (item.dueAt) {
      const isEn = getLocale() === 'en';
      const days = daysUntilIso(item.dueAt);
      if (days !== null && days >= 0 && days <= 7) {
        if (isEn) {
          return `Inspection due ${days === 0 ? 'today' : `in ${days} ${days === 1 ? 'day' : 'days'}`}`;
        }
        return `Vistoria vence em ${days === 0 ? 'hoje' : `${days} ${days === 1 ? 'dia' : 'dias'}`}`;
      }
      const overdue = daysOverdueIso(item.dueAt);
      if (overdue !== null && overdue > 0) {
        if (isEn) {
          return `Inspection overdue by ${overdue} ${overdue === 1 ? 'day' : 'days'}`;
        }
        return `Vistoria atrasada em ${overdue} ${overdue === 1 ? 'dia' : 'dias'}`;
      }
    }
    return null;
  },
  po_pending: (item) => {
    const amount = extractAmount(item);
    if (amount) {
      return getLocale() === 'en'
        ? `Purchase of ${amount} awaiting approval`
        : `Compra de ${amount} aguarda aprovação`;
    }
    return null;
  },
  doc_expiring: (item) => {
    if (item.dueAt) {
      const isEn = getLocale() === 'en';
      const days = daysUntilIso(item.dueAt);
      if (days !== null && days >= 0 && days <= 14) {
        if (isEn) {
          return `Document expires ${days === 0 ? 'today' : `in ${days} ${days === 1 ? 'day' : 'days'}`}`;
        }
        return `Documento vence em ${days === 0 ? 'hoje' : `${days} ${days === 1 ? 'dia' : 'dias'}`}`;
      }
    }
    return null;
  },
};

/**
 * Converts a dot.case or snake_case technical string into a Title Case
 * PT-BR approximation as a last-resort heuristic. Example:
 *   "budget.overrun.warn" → "Budget Overrun Warn" (better than the raw string,
 *   but callers should prefer KIND_REGEX_BLOCKLIST matches).
 */
function technicalToTitleCase(s: string): string {
  return s
    .split(/[._-]+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

/**
 * humanizeTitle(item) → human PT-BR title string (NEVER leaks kind enums).
 *
 * Resolution order:
 *   1. KIND_TITLE_OVERRIDES for context-enriched copy (FVS vence em 2 dias, etc.)
 *   2. item.title sanitized via sanitizeImpactReason (passes when human PT-BR)
 *   3. KIND_REGEX_BLOCKLIST (maps known technical titles)
 *   4. humanizeKind(item.kind).label (always human)
 *
 * Step 1 is skipped when item.title is already human so the override only
 * fires when the fallback is needed.
 */
export function humanizeTitle(item: InboxItemDTO): string {
  // Step 1: context-enriched override (only for specific kinds where evidence helps)
  const override = KIND_TITLE_OVERRIDES[item.kind as InboxItemKind];
  if (override) {
    const result = override(item);
    if (result) return result;
  }

  // Step 2a: HTTP error title → operational PT-BR copy (before sanitizeImpactReason
  // which would block all HTTP error strings as technical)
  const httpHuman = httpErrorToHuman(item.title);
  if (httpHuman) return httpHuman;

  // Step 2b: item.title from the backend when it's already human-readable PT-BR
  const clean = sanitizeImpactReason(item.title);
  if (clean) return clean;

  // Step 3: known technical title → curated label
  const isEn = getLocale() === 'en';
  const titleStr = item.title ?? '';
  for (const entry of KIND_REGEX_BLOCKLIST) {
    if (entry.pattern.test(titleStr)) return isEn ? entry.en : entry.pt;
  }

  // Step 4: kind enum → PT-BR label (guaranteed human, never technical)
  return humanizeKind(item.kind).label;
}

// ─── Impact sentences ─────────────────────────────────────────────────

/**
 * Returns an operational impact sentence based on item type and data.
 * Prioritizes item fields (evidence, reason) before fallbacks.
 * Locale is resolved at call time.
 */
export function humanizeImpact(item: InboxItemDTO): string {
  const isEn = getLocale() === 'en';
  const kind = item.kind as InboxItemKind;

  // Extract days/hours from evidence or dates
  const daysSince = item.createdAt ? daysSinceIso(item.createdAt) : null;

  switch (kind) {
    case 'client_error_open':
      if (daysSince !== null && daysSince >= 1) {
        return isEn
          ? `The client has had no update for ${daysSince} ${daysSince === 1 ? 'day' : 'days'}.`
          : `O cliente está há ${daysSince} ${daysSince === 1 ? 'dia' : 'dias'} sem atualização.`;
      }
      return isEn
        ? 'The client may be waiting for an update.'
        : 'O cliente pode estar esperando por uma atualização.';

    case 'rdo_missing':
      return isEn
        ? "Yesterday's daily site log has not been created yet."
        : 'O diário de obra de ontem ainda não foi criado.';

    case 'fvs_overdue': {
      const overdueDays = item.dueAt ? daysOverdueIso(item.dueAt) : null;
      if (overdueDays !== null && overdueDays > 0) {
        return isEn
          ? `The inspection is ${overdueDays} ${overdueDays === 1 ? 'day' : 'days'} overdue.`
          : `A vistoria está atrasada em ${overdueDays} ${overdueDays === 1 ? 'dia' : 'dias'}.`;
      }
      return isEn
        ? 'An inspection needs to be completed.'
        : 'Uma vistoria precisa ser concluída.';
    }

    case 'po_pending': {
      const amount = extractAmount(item);
      if (amount) {
        return isEn
          ? `Purchase of ${amount} is awaiting approval.`
          : `Compra de ${amount} aguarda aprovação.`;
      }
      return isEn
        ? 'A purchase order needs approval.'
        : 'Uma ordem de compra precisa de aprovação.';
    }

    case 'rnc_unassigned':
      return isEn
        ? 'An open non-conformance has no assignee yet.'
        : 'Uma não conformidade aberta ainda não tem responsável.';

    case 'doc_expiring': {
      if (item.dueAt) {
        const daysLeft = daysUntilIso(item.dueAt);
        if (daysLeft !== null && daysLeft >= 0) {
          return isEn
            ? `Document expires in ${daysLeft} ${daysLeft === 1 ? 'day' : 'days'}.`
            : `Documento vence em ${daysLeft} ${daysLeft === 1 ? 'dia' : 'dias'}.`;
        }
      }
      return isEn
        ? 'An important document is close to expiry.'
        : 'Um documento importante está próximo do vencimento.';
    }

    case 'anomaly_high':
    case 'anomaly_pending':
      return isEn
        ? 'An above-expected deviation was identified on this site.'
        : 'Um desvio acima do esperado foi identificado na obra.';

    case 'proposal_pending':
      // 2026-05-17 fix · NEVER return raw `item.reason` here.
      // Production was leaking `Decisão pendente · drift_resolve` and
      // `Anomalia: stage.slip.warn` straight into the Inbox card.
      // sanitizeImpactReason filters technical strings (cron:..., D1_ERROR,
      // snake_case kinds, FOREIGN KEY, SQLITE_*) and only returns clean copy.
      return (
        sanitizeImpactReason(item.reason) ??
        (isEn
          ? 'An automatic suggestion is waiting for your decision.'
          : 'Uma sugestão automática aguarda sua decisão.')
      );

    case 'self_heal_failed':
      return isEn
        ? 'An automatic fix could not be applied and needs manual review.'
        : 'Uma correção automática não pôde ser aplicada e precisa de revisão manual.';

    case 'medicao_draft':
      return isEn
        ? 'A measurement is in draft and has not been sent for approval.'
        : 'Uma medição está em rascunho e não foi enviada para aprovação.';

    default:
      return (
        sanitizeImpactReason(item.reason) ??
        (isEn ? 'An item needs your attention.' : 'Um item precisa da sua atenção.')
      );
  }
}

// ─── HTTP error title sanitizer ───────────────────────────────────────
//
// Wave 4xx/5xx fix (2026-05-17): Inbox was showing raw titles like
// "5xx em /v1/projects/.../photo-gallery" to the PM.
// httpErrorToHuman() maps HTTP error patterns to operational PT-BR copy.

type HttpRouteEntry = { pattern: RegExp; pt: string; en: string };

const HTTP_ROUTE_LABELS: Array<HttpRouteEntry> = [
  { pattern: /\/photo-gallery/, pt: 'Falha ao carregar fotos da obra',         en: 'Failed to load site photos' },
  { pattern: /\/drawings/,      pt: 'Falha ao carregar pranchas da obra',      en: 'Failed to load drawings' },
  { pattern: /\/verba/,         pt: 'Falha ao carregar verba',                 en: 'Failed to load budget allowance' },
  { pattern: /\/stock/,         pt: 'Falha ao carregar estoque',               en: 'Failed to load inventory' },
  { pattern: /\/fvs/,           pt: 'Falha ao carregar vistorias',             en: 'Failed to load inspections' },
  { pattern: /\/rnc/,           pt: 'Falha ao carregar não conformidades',     en: 'Failed to load non-conformances' },
  { pattern: /\/budget/,        pt: 'Falha ao carregar orçamento',             en: 'Failed to load budget' },
  { pattern: /\/daily-logs/,    pt: 'Falha ao carregar RDOs',                  en: 'Failed to load daily logs' },
  { pattern: /\/atividade/,     pt: 'Falha ao carregar atividade da obra',     en: 'Failed to load site activity' },
  { pattern: /\/inbox/,         pt: 'Falha ao carregar caixa de entrada',      en: 'Failed to load inbox' },
  { pattern: /\/comando/,       pt: 'Falha ao carregar Comando',               en: 'Failed to load Command' },
];

/** HTTP error title patterns (5xx em /v1/..., 4xx em /v1/..., HTTP 500 em /v1/..., Failed to fetch /v1/...) */
const HTTP_ERROR_TITLE_REGEX =
  /^(?:(?:HTTP\s+)?[45]\d{2}|(?:[45]xx))\s+em\s+\/v1\//i;

const FAILED_TO_FETCH_REGEX = /^Failed to fetch\s+\/v1\//i;

/**
 * httpErrorToHuman(rawTitle) → operational PT-BR label, or null when not an HTTP error.
 *
 * Maps raw titles like "5xx em /v1/projects/.../photo-gallery" to
 * "Falha ao carregar fotos da obra". Returns null when rawTitle does not
 * match any HTTP error pattern so callers can chain further checks.
 */
export function httpErrorToHuman(rawTitle: string | null | undefined): string | null {
  if (!rawTitle) return null;
  const s = rawTitle.trim();
  const isHttpError = HTTP_ERROR_TITLE_REGEX.test(s) || FAILED_TO_FETCH_REGEX.test(s);
  if (!isHttpError) return null;
  const isEn = getLocale() === 'en';
  for (const entry of HTTP_ROUTE_LABELS) {
    if (entry.pattern.test(s)) return isEn ? entry.en : entry.pt;
  }
  return isEn ? 'Technical operation failure' : 'Falha técnica na operação';
}

// ─── Technical-string sanitizer ───────────────────────────────────────
//
// 2026-05-17 · Inbox / Hoje regression fix.
// Backend writes operational reasons in human PT-BR most of the time, but
// some upstream sources (cron jobs, self-heal pipeline, anomaly engine)
// pass back raw kind codes (`drift_resolve`), error categories
// (`D1_ERROR · FOREIGN KEY constraint failed`), or job names
// (`cron:stalled_projects`). Those strings cannot reach a PM.
//
// `sanitizeImpactReason` returns:
//   - the trimmed reason when it looks human (sentence-cased PT-BR)
//   - `null` when the reason is technical — caller falls back to a
//     curated phrase for the kind

const TECHNICAL_PATTERNS = [
  /\bcron[:_-]/i,
  /\bD1_ERROR\b/,
  /\bSQLITE\w*\b/,
  /\bFOREIGN[_ ]KEY\b/i,
  /\bself[_-]?heal\b/i,
  /\bdrift[_-]/i,
  /\banomaly_resolve\b/i,
  /\bworkflow_self_heal\b/i,
  /\bPM_PROPOSALS\b/i,
  /\bpayload_json\b/i,
  /^[a-z][a-z0-9]*(?:[._-][a-z0-9]+)+$/i, // snake_case / dot.case enum-like
  /\bstacktrace\b/i,
  /\b[A-Z]{2,}_[A-Z_]+\b/, // CONSTANT_CASE
  /\btier\s*[0-9]\b/i,
  // Wave POS · 2026-05-17 · additional domain patterns the backend leaks
  /\b(stage|budget|rdo|cost|quality|supplier|workflow|trigger|policy|schedule|signal)\.[a-z][a-z._-]+/i,
  // Wave 48 · 2026-05-23 · new patterns discovered in smoke
  /\bSLA\s+breach\b/i,   // English technical term leaking from housy_nudges body
  /\bid=[a-z0-9]{16,}\b/i, // raw id= patterns like "id=01kq8q8txnq07fa4t43crmczng"
  /\b[0-9a-hjkmnp-tv-z]{26}\b/i, // ULID (26 chars, Crockford base32)
  /\bapproval_link_expired\b/i,
  /\bpo_delivery_overdue\b/i,
  /\brdo_draft_stale\b/i,
];

export function sanitizeImpactReason(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  for (const re of TECHNICAL_PATTERNS) {
    if (re.test(trimmed)) return null;
  }
  // Very long strings probably contain a stack trace mixed with text.
  if (trimmed.length > 240) return null;
  return trimmed;
}

/**
 * Returns true when a string carries operational meaning a PM can act on.
 * Inverse of sanitizeImpactReason.
 */
export function isReasonHuman(raw: string | null | undefined): boolean {
  return sanitizeImpactReason(raw) !== null;
}

// ─── Suggestion sentences ─────────────────────────────────────────────

/**
 * Returns a concrete action suggestion for the PM.
 * Locale is resolved at call time.
 */
export function humanizeSuggestion(item: InboxItemDTO): string {
  const isEn = getLocale() === 'en';
  const kind = item.kind as InboxItemKind;

  switch (kind) {
    case 'client_error_open':
      return isEn
        ? 'Send a weekly summary to the client.'
        : 'Enviar resumo da semana para o cliente.';

    case 'rdo_missing':
      return isEn
        ? "Create yesterday's daily log now."
        : 'Criar o RDO de ontem agora.';

    case 'fvs_overdue':
      return isEn
        ? 'Complete the inspection today.'
        : 'Concluir a vistoria hoje.';

    case 'po_pending':
      return isEn
        ? 'Review and approve the purchase order.'
        : 'Revisar e aprovar a ordem de compra.';

    case 'rnc_unassigned':
      return isEn
        ? 'Assign someone to the non-conformance.'
        : 'Designar um responsável pela não conformidade.';

    case 'doc_expiring':
      return isEn
        ? 'Renew the document before it expires.'
        : 'Renovar o documento antes do vencimento.';

    case 'anomaly_high':
    case 'anomaly_pending':
      return isEn
        ? 'Review the deviation and log the justification.'
        : 'Verificar o desvio e registrar a justificativa.';

    case 'proposal_pending':
      return isEn
        ? 'Review the suggestion and make a decision.'
        : 'Avaliar a sugestão e tomar uma decisão.';

    case 'self_heal_failed':
      return isEn
        ? 'Check the item and apply the fix manually.'
        : 'Verificar o ponto e aplicar a correção manualmente.';

    case 'medicao_draft':
      return isEn
        ? 'Finalize the measurement and submit for approval.'
        : 'Finalizar a medição e enviar para aprovação.';

    default:
      return isEn ? 'Review and resolve the item.' : 'Verificar e resolver o item.';
  }
}

// ─── Primary button label ─────────────────────────────────────────────

/**
 * Returns the primary action button label for the item type.
 * Operational, never technical. Locale is resolved at call time.
 */
export function humanizePrimaryLabel(item: InboxItemDTO): string {
  const isEn = getLocale() === 'en';
  const kind = item.kind as InboxItemKind;

  switch (kind) {
    case 'client_error_open':
      return isEn ? 'Send' : 'Enviar';
    case 'rdo_missing':
      return isEn ? 'Create log' : 'Criar RDO';
    case 'fvs_overdue':
      return isEn ? 'Complete inspection' : 'Concluir vistoria';
    case 'po_pending':
      return isEn ? 'Approve' : 'Aprovar';
    case 'rnc_unassigned':
      return isEn ? 'Assign' : 'Atribuir';
    case 'doc_expiring':
      return isEn ? 'Renew' : 'Renovar';
    case 'medicao_draft':
      return isEn ? 'Finalize' : 'Finalizar';
    case 'proposal_pending':
      return isEn ? 'Decide' : 'Decidir';
    case 'self_heal_failed':
      return isEn ? 'Review' : 'Verificar';
    default:
      return isEn ? 'Resolve' : 'Resolver';
  }
}

// ─── Relative time ────────────────────────────────────────────────────

/**
 * Converts an ISO date to a relative time phrase.
 * PT examples: "há 2 horas", "ontem", "há 3 dias", "em 2 horas", "agora"
 * EN examples: "2 hours ago", "yesterday", "3 days ago", "in 2 hours", "just now"
 * Locale is resolved at call time.
 */
export function relativeTime(iso: string): string {
  if (!iso) return '';
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return iso;

  const isEn = getLocale() === 'en';
  const diffMs = t - Date.now();
  const absDiffMs = Math.abs(diffMs);
  const isPast = diffMs < 0;

  const minutes = Math.floor(absDiffMs / 60_000);
  const hours = Math.round(absDiffMs / 3_600_000);
  const days = Math.round(absDiffMs / 86_400_000);

  if (minutes < 1) return isEn ? 'just now' : 'agora';

  if (isPast) {
    if (isEn) {
      if (minutes < 60) return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'} ago`;
      if (hours < 24) return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
      if (days === 1) return 'yesterday';
      return `${days} days ago`;
    }
    if (minutes < 60) return `há ${minutes} ${minutes === 1 ? 'minuto' : 'minutos'}`;
    if (hours < 24) return `há ${hours} ${hours === 1 ? 'hora' : 'horas'}`;
    if (days === 1) return 'ontem';
    return `há ${days} dias`;
  } else {
    if (isEn) {
      if (minutes < 60) return `in ${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`;
      if (hours < 24) return `in ${hours} ${hours === 1 ? 'hour' : 'hours'}`;
      if (days === 1) return 'tomorrow';
      return `in ${days} days`;
    }
    if (minutes < 60) return `em ${minutes} ${minutes === 1 ? 'minuto' : 'minutos'}`;
    if (hours < 24) return `em ${hours} ${hours === 1 ? 'hora' : 'horas'}`;
    if (days === 1) return 'amanhã';
    return `em ${days} dias`;
  }
}

// ─── Internal helpers ─────────────────────────────────────────────────

function daysSinceIso(iso: string): number | null {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return null;
  const diffMs = Date.now() - t;
  return Math.max(0, Math.floor(diffMs / 86_400_000));
}

function daysOverdueIso(iso: string): number | null {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return null;
  const diffMs = Date.now() - t;
  return Math.max(0, Math.floor(diffMs / 86_400_000));
}

function daysUntilIso(iso: string): number | null {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return null;
  const diffMs = t - Date.now();
  return Math.max(0, Math.floor(diffMs / 86_400_000));
}

function extractAmount(item: InboxItemDTO): string | null {
  const chip = item.evidence.find(
    (e) =>
      e.label.toLowerCase().includes('valor') ||
      e.label.toLowerCase().includes('total') ||
      e.label.toLowerCase().includes('amount'),
  );
  return chip?.value ?? null;
}
